/**
 * <ugc-gallery> — sections/ugc-gallery.liquid
 */
(function () {
  if (customElements.get("ugc-gallery")) return;

  const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
  const NO_HOVER_QUERY = "(hover: none)";

  class UgcGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector(".ugc_grid");
      this.items = Array.from(this.querySelectorAll(".ugc_item"));
      this.cards = Array.from(this.querySelectorAll(".ugc_card"));

      if (!this.items.length) return;

      this.reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
      this.noHoverQuery = window.matchMedia(NO_HOVER_QUERY);

      this.onKeydown = this.onKeydown.bind(this);
      this.syncTrackFocus = this.syncTrackFocus.bind(this);
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.onDragStart = this.onDragStart.bind(this);
      this.onDragMove = this.onDragMove.bind(this);
      this.onDragEnd = this.onDragEnd.bind(this);
      this.onPaginationPointerDown = this.onPaginationPointerDown.bind(this);
      this.onPaginationPointerMove = this.onPaginationPointerMove.bind(this);
      this.onPaginationPointerUp = this.onPaginationPointerUp.bind(this);

      this.setupReveal();
      this.setupVideos();
      this.setupProducts();
      this.setupSlider();
    }

    disconnectedCallback() {
      if (this.revealObserver) this.revealObserver.disconnect();
      if (this.videoObserver) this.videoObserver.disconnect();
      if (this.track) {
        this.track.removeEventListener("keydown", this.onKeydown);
        this.track.removeEventListener("scroll", this.syncTrackFocus);
        this.track.removeEventListener("pointerdown", this.onDragStart);
      }
      if (this.pagination) {
        this.pagination.removeEventListener("pointerdown", this.onPaginationPointerDown);
      }
      window.removeEventListener("pointermove", this.onDragMove);
      window.removeEventListener("pointerup", this.onDragEnd);
      window.removeEventListener("pointercancel", this.onDragEnd);
      window.removeEventListener("resize", this.syncTrackFocus);
      window.removeEventListener("load", this.syncTrackFocus);
      document.removeEventListener("pointerdown", this.onDocumentPointerDown);
    }

    /* Cards fade up once, in source order, unless motion is unwelcome */
    setupReveal() {
      if (this.dataset.reveal !== "true") return;

      if (this.reducedMotionQuery.matches || !("IntersectionObserver" in window)) {
        this.items.forEach((item) => item.classList.add("is-in-view"));
        return;
      }

      this.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-in-view");
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: "0px 0px -10% 0px", threshold: 0.15 },
      );

      this.items.forEach((item) => this.revealObserver.observe(item));
    }

    setupVideos() {
      this.videoCards = this.cards.filter((card) => card.querySelector("video"));
      if (!this.videoCards.length) return;

      this.videoCards.forEach((card) => {
        const video = card.querySelector("video");
        const toggle = card.querySelector("[data-ugc-video-toggle]");

        /* Muted + inline is what lets mobile browsers autoplay at all */
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute("muted", "");
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");
        video.controls = false;

        if (toggle) {
          toggle.addEventListener("click", () => this.toggleVideo(card, video, toggle));
        }

        video.addEventListener("play", () => this.syncToggle(toggle, true));
        video.addEventListener("pause", () => this.syncToggle(toggle, false));
      });

      const wantsAutoplay =
        this.dataset.videoMode === "autoplay" && !this.reducedMotionQuery.matches;

      if (!wantsAutoplay || !("IntersectionObserver" in window)) return;

      /* Only play what is actually on screen, so scrolling never stacks players */
      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const card = entry.target;
            const video = card.querySelector("video");
            if (!video) return;

            if (entry.isIntersecting) {
              this.playVideo(card, video);
            } else {
              video.pause();
              card.dataset.autoplaying = "false";
            }
          });
        },
        { threshold: 0.4 },
      );

      this.videoCards.forEach((card) => this.videoObserver.observe(card));
    }

    playVideo(card, video) {
      const attempt = video.play();
      if (!attempt || typeof attempt.catch !== "function") return;

      /* Blocked autoplay simply leaves the play button visible */
      attempt
        .then(() => {
          card.dataset.autoplaying = "true";
        })
        .catch(() => {
          card.dataset.autoplaying = "false";
        });
    }

    toggleVideo(card, video, toggle) {
      if (video.paused) {
        this.playVideo(card, video);
      } else {
        video.pause();
        card.dataset.autoplaying = "false";
      }

      this.syncToggle(toggle, !video.paused);
    }

    syncToggle(toggle, isPlaying) {
      if (!toggle) return;

      toggle.setAttribute("aria-pressed", isPlaying ? "true" : "false");

      const label = isPlaying ? toggle.dataset.labelPause : toggle.dataset.labelPlay;
      if (label) toggle.setAttribute("aria-label", label);
    }

    /* Without hover there is nothing to reveal on, so the first tap opens the card */
    setupProducts() {
      this.products = Array.from(this.querySelectorAll(".ugc_product:not(.ugc_product--below)"));
      if (!this.products.length) return;

      this.products.forEach((product) => {
        product.addEventListener("click", (event) => {
          if (!this.noHoverQuery.matches) return;
          if (product.classList.contains("is-open")) return;

          event.preventDefault();
          this.closeProducts(product);
          product.classList.add("is-open");
        });
      });

      document.addEventListener("pointerdown", this.onDocumentPointerDown);
    }

    onDocumentPointerDown(event) {
      if (!this.products || !this.products.length) return;
      if (event.target.closest(".ugc_product")) return;

      this.closeProducts();
    }

    closeProducts(except) {
      this.products.forEach((product) => {
        if (product !== except) product.classList.remove("is-open");
      });
    }

    /* The track scrolls on every breakpoint, so it stays reachable by keyboard whenever it overflows */
    setupSlider() {
      if (!this.track) return;

      this.pagination = this.querySelector("[data-ugc-pagination]");
      this.paginationFill = this.querySelector("[data-ugc-pagination-fill]");

      this.track.addEventListener("pointerdown", this.onDragStart);
      this.track.addEventListener("dragstart", (event) => {
        if (this.isDragging) event.preventDefault();
      });

      if (this.pagination) {
        this.pagination.addEventListener("pointerdown", this.onPaginationPointerDown);
      }

      this.track.addEventListener("keydown", this.onKeydown);
      this.track.addEventListener("scroll", this.syncTrackFocus, { passive: true });
      window.addEventListener("resize", this.syncTrackFocus, { passive: true });
      window.addEventListener("load", this.syncTrackFocus);
      this.syncTrackFocus();
    }

    syncPagination() {
      if (!this.pagination || !this.paginationFill) return;

      if (!this.isScrollable) {
        this.pagination.hidden = true;
        return;
      }

      this.pagination.hidden = false;

      const scrollable = this.track.scrollWidth - this.track.clientWidth;
      const thumb = Math.max((this.track.clientWidth / this.track.scrollWidth) * 100, 15);
      const progress = scrollable > 0 ? this.track.scrollLeft / scrollable : 0;

      this.paginationFill.style.width = thumb + "%";
      this.paginationFill.style.left = progress * (100 - thumb) + "%";
    }

    get isScrollable() {
      return this.track ? this.track.scrollWidth - this.track.clientWidth > 2 : false;
    }

    /* Pointer drag, because a native scroller does not follow a held mouse button */
    onDragStart(event) {
      if (event.pointerType === "touch") return;
      if (event.button !== 0) return;
      if (!this.isScrollable) return;
      if (event.target.closest("a, button, video")) return;

      this.isDragging = true;
      this.dragMoved = false;
      this.dragStartX = event.clientX;
      this.dragStartScroll = this.track.scrollLeft;

      this.track.classList.add("is-dragging");
      this.track.style.scrollSnapType = "none";

      window.addEventListener("pointermove", this.onDragMove);
      window.addEventListener("pointerup", this.onDragEnd);
      window.addEventListener("pointercancel", this.onDragEnd);
    }

    onDragMove(event) {
      if (!this.isDragging) return;

      const distance = event.clientX - this.dragStartX;
      if (Math.abs(distance) > 4) this.dragMoved = true;

      this.track.scrollLeft = this.dragStartScroll - distance;
    }

    onDragEnd() {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.track.classList.remove("is-dragging");
      this.track.style.scrollSnapType = "";

      window.removeEventListener("pointermove", this.onDragMove);
      window.removeEventListener("pointerup", this.onDragEnd);
      window.removeEventListener("pointercancel", this.onDragEnd);

      /* A drag must not open the product the pointer happened to finish on */
      if (this.dragMoved) {
        const swallow = (event) => event.preventDefault();
        this.track.addEventListener("click", swallow, { capture: true, once: true });
        window.setTimeout(() => {
          this.track.removeEventListener("click", swallow, { capture: true });
        }, 0);
      }
    }

    /* The progress bar doubles as a scrubber */
    onPaginationPointerDown(event) {
      if (!this.isScrollable) return;

      event.preventDefault();
      this.pagination.setPointerCapture?.(event.pointerId);
      this.scrubTo(event.clientX);

      this.pagination.addEventListener("pointermove", this.onPaginationPointerMove);
      this.pagination.addEventListener("pointerup", this.onPaginationPointerUp);
      this.pagination.addEventListener("pointercancel", this.onPaginationPointerUp);
    }

    onPaginationPointerMove(event) {
      this.scrubTo(event.clientX);
    }

    onPaginationPointerUp(event) {
      this.pagination.releasePointerCapture?.(event.pointerId);
      this.pagination.removeEventListener("pointermove", this.onPaginationPointerMove);
      this.pagination.removeEventListener("pointerup", this.onPaginationPointerUp);
      this.pagination.removeEventListener("pointercancel", this.onPaginationPointerUp);
    }

    scrubTo(clientX) {
      const rect = this.pagination.getBoundingClientRect();
      if (!rect.width) return;

      const fraction = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
      const scrollable = this.track.scrollWidth - this.track.clientWidth;
      const behavior = this.reducedMotionQuery.matches ? "auto" : "smooth";

      this.track.scrollTo({ left: fraction * scrollable, behavior });
    }

    syncTrackFocus() {
      if (!this.track) return;

      if (this.isScrollable) {
        this.track.setAttribute("tabindex", "0");
        this.track.setAttribute("role", "region");
        this.track.setAttribute("aria-label", this.trackLabel);
      } else {
        this.track.removeAttribute("tabindex");
        this.track.removeAttribute("role");
        this.track.removeAttribute("aria-label");
      }

      this.syncPagination();
    }

    get trackLabel() {
      const heading = this.closest(".ugc")?.querySelector(".ugc_heading");
      return heading ? heading.textContent.trim() : "UGC gallery";
    }

    onKeydown(event) {
      if (!this.isScrollable) return;
      if (event.target !== this.track) return;
      if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;

      event.preventDefault();

      const step = this.items[0] ? this.items[0].getBoundingClientRect().width + 12 : 200;
      const direction = event.key === "ArrowRight" ? 1 : -1;
      const behavior = this.reducedMotionQuery.matches ? "auto" : "smooth";

      this.track.scrollBy({ left: step * direction, behavior });
    }
  }

  customElements.define("ugc-gallery", UgcGallery);
})();
